<script setup lang="ts">
import {
  CommandPaletteProductSearchDocument,
  type Product,
} from '~/graphql/operations'
import { ComboboxOption } from '@headlessui/vue'

const router = useRouter()

const props = defineProps<{
  query: string
}>()
const user = useUser()

const productSearchQuery = useQuery({
  query: CommandPaletteProductSearchDocument,
  variables: computed(() => ({
    search: props.query,
    searchTags: ['productNumber', 'productName'],
    translationLanguageId: user.value?.languageId,
  })),
  pause: true,
})

const fastProductSearchQuery = useQuery({
  query: CommandPaletteProductSearchDocument,
  variables: computed(() => ({
    search: props.query,
    searchTags: ['productNumber'],
    translationLanguageId: user.value?.languageId,
  })),
  pause: true,
})

const transformProduct = (product: Product) => {
  const manufacturerName =
    product.manufacturer?.defaultText?.name ||
    product.manufacturer?.manufacturerTexts?.[0]?.name
  const displayName =
    product.defaultText?.name ||
    product.productTexts?.[0]?.name ||
    product.productNumber
  const description = manufacturerName
    ? `${manufacturerName} - ${product.productNumber}`
    : product.productNumber

  return {
    id: `product-search-${product.id}`,
    name: displayName,
    description,
    icon: 'i-icon-park-outline:ad-product',
    action: () => router.push(`/products/${product.id}`),
  }
}

const transformedFastProducts = computed(() => {
  return (
    fastProductSearchQuery.data.value?.productsWhere.data.map((product: any) =>
      transformProduct(product),
    ) || []
  )
})

const transformedProducts = computed(() => {
  return (
    productSearchQuery.data.value?.productsWhere.data.map((product: any) =>
      transformProduct(product),
    ) || []
  )
})

const searchResults = computed(() => {
  if (
    transformedFastProducts.value.length &&
    productSearchQuery.fetching.value
  ) {
    return transformedFastProducts.value
  } else if (transformedProducts.value.length) {
    return transformedProducts.value
  }
  return []
})

const items = computed(() => {
  if (fastProductSearchQuery.fetching.value) return []
  if (!props.query.length) return []
  return searchResults.value
})
const debounceInProgress = ref(false)
let debouncer = null
watch(
  () => props.query,
  () => {
    if (debouncer) {
      clearTimeout(debouncer)
    }
    debounceInProgress.value = true
    debouncer = setTimeout(async () => {
      debounceInProgress.value = false
      Promise.all([
        fastProductSearchQuery.executeQuery(),
        productSearchQuery.executeQuery(),
      ])
    }, 300)
  },
)
const isLoading = computed(
  () =>
    debounceInProgress.value ||
    productSearchQuery.fetching.value ||
    fastProductSearchQuery.fetching.value,
)
</script>
<template>
  <LoadingBar v-if="isLoading" class="text-primary w-full" />
  <div
    class="px-4 py-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase d"
    :class="{
      'pt-3': isLoading,
      'pt-4': !isLoading,
    }"
  >
    Products
  </div>

  <ComboboxOption
    v-if="items.length"
    v-for="item in items"
    :key="item.id"
    :value="item"
    v-slot="{ active }"
    as="template"
  >
    <CommandPaletteItem :item="item" :active="active" />
  </ComboboxOption>
  <CommandPaletteMessagesSearching v-else-if="isLoading && !items.length" />
  <CommandPaletteMessagesNothingFound v-else-if="query.length" />

  <div v-else-if="!query.length" class="py-8 px-6 text-center text-sm">
    <p class="mt-2 text-gray-500">
      Search for products by entering the product number or name, e.g. "product
      123456" or "product abc"
    </p>
  </div>
</template>
